import { useState, useEffect } from 'react';
import { API_URL } from '../config';
import { Cloud, Plus, Trash2, RefreshCw, ExternalLink, Loader, CheckCircle, AlertCircle, Mail, MessageSquare, FileText, Zap } from 'lucide-react';

interface APIConnection {
  connection_id: string;
  name: string;
  api_type: string;
  status: string;
  created_at: string;
}

interface CloudFile {
  id: string;
  name: string;
  mimeType?: string;
  size?: number;
  modifiedTime?: string;
}

export default function ServicesAPIPanel() {
  const [connections, setConnections] = useState<APIConnection[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [selectedAPI, setSelectedAPI] = useState<string>('google_drive');
  
  // 📂 File Browser states
  const [showFileBrowser, setShowFileBrowser] = useState(false);
  const [browserConnection, setBrowserConnection] = useState<APIConnection | null>(null);
  const [cloudFiles, setCloudFiles] = useState<CloudFile[]>([]);
  const [selectedFiles, setSelectedFiles] = useState<Set<string>>(new Set());
  const [isLoadingFiles, setIsLoadingFiles] = useState(false);
  const [indexName, setIndexName] = useState('');
  const [isIndexing, setIsIndexing] = useState(false);
  const [trackingId, setTrackingId] = useState<string | null>(null);
  const [pollingIntervalRef, setPollingIntervalRef] = useState<number | null>(null);
  
  // Form states
  const [formData, setFormData] = useState({
    name: '',
    // Google Drive / Gmail
    // Notion
    notion_api_key: '',
    // Dropbox
    dropbox_token: '',
    // Confluence
    confluence_url: '',
    confluence_username: '',
    confluence_token: '',
    // Slack
    slack_bot_token: ''
  });

  useEffect(() => {
    loadConnections();
    
    // Cleanup polling on unmount
    return () => {
      if (pollingIntervalRef) {
        clearInterval(pollingIntervalRef);
        console.log('🧹 Cleaned up polling interval on unmount');
      }
    };
  }, [pollingIntervalRef]);

  // 📂 File Browser Functions
  const openFileBrowser = async (connection: APIConnection) => {
    setBrowserConnection(connection);
    setShowFileBrowser(true);
    setSelectedFiles(new Set());
    setIndexName(`${connection.name}_index`);
    await loadFiles(connection);
  };

  const loadFiles = async (connection: APIConnection) => {
    setIsLoadingFiles(true);
    try {
      let endpoint = '';
      if (connection.api_type === 'google_drive') {
        endpoint = `/api/api-connectors/google-drive/files/${connection.connection_id}`;
      } else if (connection.api_type === 'dropbox') {
        endpoint = `/api/api-connectors/dropbox/files/${connection.connection_id}`;
      }

      if (!endpoint) {
        alert('❌ File browsing non supportato per questo tipo di connessione');
        return;
      }

      const response = await fetch(`${API_URL}${endpoint}`);
      if (!response.ok) throw new Error('Failed to load files');
      const data = await response.json();
      
      setCloudFiles(data.files || []);
    } catch (error) {
      console.error('Error loading files:', error);
      alert('❌ Errore caricamento file');
    } finally {
      setIsLoadingFiles(false);
    }
  };

  const toggleFileSelection = (fileId: string) => {
    const newSelected = new Set(selectedFiles);
    if (newSelected.has(fileId)) {
      newSelected.delete(fileId);
    } else {
      newSelected.add(fileId);
    }
    setSelectedFiles(newSelected);
  };

  const toggleSelectAll = () => {
    if (selectedFiles.size === cloudFiles.length) {
      setSelectedFiles(new Set());
    } else {
      setSelectedFiles(new Set(cloudFiles.map(f => f.id)));
    }
  };

  const handleIndexSelectedFiles = async () => {
    if (!browserConnection) return;
    if (selectedFiles.size === 0) {
      alert('❌ Seleziona almeno un file');
      return;
    }
    if (!indexName.trim()) {
      alert('❌ Inserisci il nome dell\'indice');
      return;
    }

    setIsIndexing(true);
    try {
      const filesToIndex = cloudFiles.filter(f => selectedFiles.has(f.id));
      
      const response = await fetch(`${API_URL}/api/api-connectors/index-files`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          connection_id: browserConnection.connection_id,
          api_type: browserConnection.api_type,
          files: filesToIndex,
          index_name: indexName,
          // Parametri RAG (default)
          embed_model: 'BAAI/bge-m3',
          extractor: 'pymupdf',
          chunk_size: 500,
          overlap: 50,
          use_semantic_chunking: false,
          similarity_threshold: 0.85,
          min_chunk_chars: 100,
          max_chunk_chars: 1500,
          use_ocr_fallback: false,
          use_presidio: false
        })
      });

      if (!response.ok) throw new Error('Indexing failed');
      const data = await response.json();
      
      setTrackingId(data.tracking_id);
      alert(`✅ Indicizzazione avviata! Tracking ID: ${data.tracking_id}\n${data.message}`);
      
      // Start polling for status
      pollIndexingStatus(data.tracking_id);
      
    } catch (error: any) {
      console.error('Error indexing files:', error);
      alert(`❌ Errore: ${error.message}`);
    } finally {
      setIsIndexing(false);
    }
  };

  const pollIndexingStatus = (trackingId: string) => {
    // Clear previous interval if exists
    if (pollingIntervalRef) {
      clearInterval(pollingIntervalRef);
    }
    
    const interval = setInterval(async () => {
      try {
        const response = await fetch(`${API_URL}/api/api-connectors/sync-status/${trackingId}`);
        if (!response.ok) {
          clearInterval(interval);
          setPollingIntervalRef(null);
          return;
        }
        
        const data = await response.json();
        
        if (data.status === 'completed') {
          clearInterval(interval);
          setPollingIntervalRef(null);
          alert(`✅ Indicizzazione completata!\n- File processati: ${data.processed_files}\n- Chunks: ${data.chunks_count}\n- File falliti: ${data.failed_files || 0}`);
          setShowFileBrowser(false);
          setTrackingId(null);
        } else if (data.status === 'failed') {
          clearInterval(interval);
          setPollingIntervalRef(null);
          alert(`❌ Indicizzazione fallita: ${data.error}`);
          setTrackingId(null);
        }
      } catch (error) {
        console.error('Error polling status:', error);
        clearInterval(interval);
        setPollingIntervalRef(null);
      }
    }, 3000); // Poll every 3 seconds
    
    setPollingIntervalRef(interval);
  };

  const loadConnections = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/api-connectors/list`);
      if (!response.ok) throw new Error('Failed to load connections');
      const data = await response.json();
      setConnections(data.connections || []);
    } catch (error) {
      console.error('Error loading connections:', error);
      alert('❌ Errore caricamento connessioni');
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleAuth = async () => {
    try {
      const response = await fetch(`${API_URL}/api/api-connectors/google-drive/auth-url`);
      const data = await response.json();
      
      if (data.auth_url) {
        window.open(data.auth_url, '_blank');
        alert('✅ Autorizza Google nel browser, poi torna qui e ricarica le connessioni');
      }
    } catch (error: any) {
      alert(`❌ Errore: ${error.message}`);
    }
  };

  const handleGmailAuth = async () => {
    try {
      const response = await fetch(`${API_URL}/api/api-connectors/gmail/auth-url`);
      const data = await response.json();
      
      if (data.auth_url) {
        window.open(data.auth_url, '_blank');
        alert('✅ Autorizza Gmail nel browser, poi torna qui e ricarica le connessioni');
      }
    } catch (error: any) {
      alert(`❌ Errore: ${error.message}`);
    }
  };

  const handleConnectNotion = async () => {
    if (!formData.name || !formData.notion_api_key) {
      alert('❌ Inserisci nome e API key');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/api-connectors/notion/create?name=${encodeURIComponent(formData.name)}&api_key=${encodeURIComponent(formData.notion_api_key)}`, {
        method: 'POST'
      });
      
      if (!response.ok) throw new Error('Connection failed');
      
      alert('✅ Notion connesso!');
      setShowAddForm(false);
      loadConnections();
    } catch (error: any) {
      alert(`❌ Errore: ${error.message}`);
    }
  };

  const handleConnectDropbox = async () => {
    if (!formData.name || !formData.dropbox_token) {
      alert('❌ Inserisci nome e access token');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/api-connectors/dropbox/create?name=${encodeURIComponent(formData.name)}&access_token=${encodeURIComponent(formData.dropbox_token)}`, {
        method: 'POST'
      });
      
      if (!response.ok) throw new Error('Connection failed');
      
      alert('✅ Dropbox connesso!');
      setShowAddForm(false);
      loadConnections();
    } catch (error: any) {
      alert(`❌ Errore: ${error.message}`);
    }
  };

  const handleConnectConfluence = async () => {
    if (!formData.name || !formData.confluence_url || !formData.confluence_username || !formData.confluence_token) {
      alert('❌ Compila tutti i campi Confluence');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/api-connectors/confluence/create?name=${encodeURIComponent(formData.name)}&url=${encodeURIComponent(formData.confluence_url)}&username=${encodeURIComponent(formData.confluence_username)}&api_token=${encodeURIComponent(formData.confluence_token)}`, {
        method: 'POST'
      });
      
      if (!response.ok) throw new Error('Connection failed');
      
      alert('✅ Confluence connesso!');
      setShowAddForm(false);
      loadConnections();
    } catch (error: any) {
      alert(`❌ Errore: ${error.message}`);
    }
  };

  const handleConnectSlack = async () => {
    if (!formData.name || !formData.slack_bot_token) {
      alert('❌ Inserisci nome e bot token');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/api-connectors/slack/create?name=${encodeURIComponent(formData.name)}&bot_token=${encodeURIComponent(formData.slack_bot_token)}`, {
        method: 'POST'
      });
      
      if (!response.ok) throw new Error('Connection failed');
      
      alert('✅ Slack connesso!');
      setShowAddForm(false);
      loadConnections();
    } catch (error: any) {
      alert(`❌ Errore: ${error.message}`);
    }
  };

  const handleDeleteConnection = async (connectionId: string) => {
    if (!confirm('Vuoi eliminare questa connessione?')) return;

    try {
      const response = await fetch(`${API_URL}/api/api-connectors/delete/${connectionId}`, {
        method: 'DELETE'
      });
      
      if (!response.ok) throw new Error('Delete failed');
      
      alert('✅ Connessione eliminata');
      loadConnections();
    } catch (error: any) {
      alert(`❌ Errore: ${error.message}`);
    }
  };

  return (
    <div className="space-y-6">
      {/* 🔌 Header */}
      <div className="bg-gradient-to-r from-purple-50 to-pink-50 border-2 border-purple-200 rounded-xl shadow-md p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center">
            <Cloud className="w-7 h-7 text-purple-600 mr-3" />
            <div>
              <h2 className="text-2xl font-bold text-purple-900">🔌 Services & API Connectors</h2>
              <p className="text-sm text-purple-700 mt-1">
                Connetti Google Drive, Gmail, Notion, Dropbox, Confluence, Slack
              </p>
            </div>
          </div>
          <div className="flex space-x-2">
            <button
              onClick={loadConnections}
              disabled={isLoading}
              className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 flex items-center"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
              Ricarica
            </button>
            <button
              onClick={() => setShowAddForm(!showAddForm)}
              className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center"
            >
              <Plus className="w-4 h-4 mr-2" />
              Nuova Connessione
            </button>
          </div>
        </div>

        {/* 📊 Stats */}
        <div className="grid grid-cols-3 gap-4 mt-4">
          <div className="bg-white rounded-lg p-3 border border-purple-200">
            <div className="text-xs text-purple-600 font-medium">Connessioni Attive</div>
            <div className="text-2xl font-bold text-purple-900">{connections.length}</div>
          </div>
          <div className="bg-white rounded-lg p-3 border border-purple-200">
            <div className="text-xs text-purple-600 font-medium">API Supportate</div>
            <div className="text-2xl font-bold text-purple-900">6</div>
          </div>
          <div className="bg-white rounded-lg p-3 border border-purple-200">
            <div className="text-xs text-purple-600 font-medium">Auto-Sync</div>
            <div className="text-2xl font-bold text-purple-900">🔄</div>
          </div>
        </div>
      </div>

      {/* ➕ Form Nuova Connessione */}
      {showAddForm && (
        <div className="bg-white border-2 border-purple-200 rounded-xl shadow-md p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold text-purple-900">➕ Nuova Connessione API</h3>
            <button
              onClick={() => setShowAddForm(false)}
              className="text-gray-500 hover:text-gray-700"
            >
              ✕
            </button>
          </div>

          {/* API Type Selector */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Tipo di Servizio *
            </label>
            <select
              value={selectedAPI}
              onChange={(e) => setSelectedAPI(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500"
            >
              <optgroup label="☁️ Cloud Storage">
                <option value="google_drive">🔵 Google Drive</option>
                <option value="dropbox">📦 Dropbox</option>
              </optgroup>
              <optgroup label="📧 Communication">
                <option value="gmail">📧 Gmail</option>
                <option value="slack">💬 Slack</option>
              </optgroup>
              <optgroup label="📝 Productivity">
                <option value="notion">📝 Notion</option>
                <option value="confluence">🏢 Confluence</option>
              </optgroup>
            </select>
          </div>

          {/* Common: Name */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Nome Connessione *
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({...formData, name: e.target.value})}
              placeholder="Es: My Google Drive"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
            />
          </div>

          {/* Google Drive */}
          {selectedAPI === 'google_drive' && (
            <div className="bg-blue-50 rounded-lg p-4 mb-4">
              <h4 className="font-bold text-blue-900 mb-2">🔵 Google Drive (OAuth2)</h4>
              <p className="text-sm text-blue-700 mb-3">
                Richiede configurazione Google Cloud Console
              </p>
              <div className="space-y-2 text-xs text-blue-600">
                <p>1. Vai su: <a href="https://console.cloud.google.com/apis/credentials" target="_blank" rel="noopener noreferrer" className="underline">Google Cloud Console</a></p>
                <p>2. Crea OAuth 2.0 Client ID</p>
                <p>3. Aggiungi redirect URI: <code className="bg-blue-100 px-1">${API_URL}/api/api-connectors/google-drive/callback</code></p>
                <p>4. Imposta variabili ambiente: GOOGLE_CLIENT_ID e GOOGLE_CLIENT_SECRET</p>
              </div>
              <button
                onClick={handleGoogleAuth}
                className="w-full mt-3 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium"
              >
                🔐 Autorizza Google Drive
              </button>
            </div>
          )}

          {/* Gmail */}
          {selectedAPI === 'gmail' && (
            <div className="bg-red-50 rounded-lg p-4 mb-4">
              <h4 className="font-bold text-red-900 mb-2">📧 Gmail (OAuth2)</h4>
              <p className="text-sm text-red-700 mb-3">
                Usa le stesse credentials di Google Drive
              </p>
              <div className="space-y-2 text-xs text-red-600">
                <p>• Scope: gmail.readonly</p>
                <p>• Accede a email threads e messaggi</p>
              </div>
              <button
                onClick={handleGmailAuth}
                className="w-full mt-3 bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg text-sm font-medium"
              >
                🔐 Autorizza Gmail
              </button>
            </div>
          )}

          {/* Notion */}
          {selectedAPI === 'notion' && (
            <div className="bg-gray-50 rounded-lg p-4 mb-4">
              <h4 className="font-bold text-gray-900 mb-2">📝 Notion (API Key)</h4>
              <p className="text-sm text-gray-700 mb-3">
                Ottieni API key da: <a href="https://www.notion.so/my-integrations" target="_blank" rel="noopener noreferrer" className="underline text-blue-600">Notion Integrations</a>
              </p>
              <input
                type="password"
                value={formData.notion_api_key}
                onChange={(e) => setFormData({...formData, notion_api_key: e.target.value})}
                placeholder="secret_..."
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm mb-2"
              />
              <button
                onClick={handleConnectNotion}
                className="w-full bg-gray-800 hover:bg-gray-900 text-white px-4 py-2 rounded-lg text-sm font-medium"
              >
                🔗 Connetti Notion
              </button>
            </div>
          )}

          {/* Dropbox */}
          {selectedAPI === 'dropbox' && (
            <div className="bg-blue-50 rounded-lg p-4 mb-4">
              <h4 className="font-bold text-blue-900 mb-2">📦 Dropbox (Access Token)</h4>
              <p className="text-sm text-blue-700 mb-3">
                Ottieni token da: <a href="https://www.dropbox.com/developers/apps" target="_blank" rel="noopener noreferrer" className="underline">Dropbox Apps</a>
              </p>
              <input
                type="password"
                value={formData.dropbox_token}
                onChange={(e) => setFormData({...formData, dropbox_token: e.target.value})}
                placeholder="sl.xxx..."
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm mb-2"
              />
              <button
                onClick={handleConnectDropbox}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium"
              >
                🔗 Connetti Dropbox
              </button>
            </div>
          )}

          {/* Confluence */}
          {selectedAPI === 'confluence' && (
            <div className="bg-blue-50 rounded-lg p-4 mb-4">
              <h4 className="font-bold text-blue-900 mb-2">🏢 Confluence (API Token)</h4>
              <p className="text-sm text-blue-700 mb-3">
                Ottieni token da: <a href="https://id.atlassian.com/manage-profile/security/api-tokens" target="_blank" rel="noopener noreferrer" className="underline">Atlassian API Tokens</a>
              </p>
              <div className="space-y-2">
                <input
                  type="url"
                  value={formData.confluence_url}
                  onChange={(e) => setFormData({...formData, confluence_url: e.target.value})}
                  placeholder="https://your-domain.atlassian.net/wiki"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                />
                <input
                  type="email"
                  value={formData.confluence_username}
                  onChange={(e) => setFormData({...formData, confluence_username: e.target.value})}
                  placeholder="your-email@example.com"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                />
                <input
                  type="password"
                  value={formData.confluence_token}
                  onChange={(e) => setFormData({...formData, confluence_token: e.target.value})}
                  placeholder="API Token"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                />
              </div>
              <button
                onClick={handleConnectConfluence}
                className="w-full mt-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium"
              >
                🔗 Connetti Confluence
              </button>
            </div>
          )}

          {/* Slack */}
          {selectedAPI === 'slack' && (
            <div className="bg-purple-50 rounded-lg p-4 mb-4">
              <h4 className="font-bold text-purple-900 mb-2">💬 Slack (Bot Token)</h4>
              <p className="text-sm text-purple-700 mb-3">
                Crea un'app Slack: <a href="https://api.slack.com/apps" target="_blank" rel="noopener noreferrer" className="underline">Slack Apps</a>
              </p>
              <p className="text-xs text-purple-600 mb-2">
                Scopes richiesti: channels:history, channels:read, groups:history, groups:read
              </p>
              <input
                type="password"
                value={formData.slack_bot_token}
                onChange={(e) => setFormData({...formData, slack_bot_token: e.target.value})}
                placeholder="xoxb-..."
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm mb-2"
              />
              <button
                onClick={handleConnectSlack}
                className="w-full bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg text-sm font-medium"
              >
                🔗 Connetti Slack
              </button>
            </div>
          )}
        </div>
      )}

      {/* 📋 Lista Connessioni */}
      <div className="bg-white border-2 border-purple-200 rounded-xl shadow-md p-6">
        <h3 className="text-lg font-bold text-purple-900 mb-4">📋 Connessioni Attive</h3>
        
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader className="w-8 h-8 text-purple-600 animate-spin" />
          </div>
        ) : connections.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            Nessuna connessione API configurata
          </div>
        ) : (
          <div className="space-y-3">
            {connections.map((conn) => (
              <div
                key={conn.connection_id}
                className="flex items-center justify-between p-4 border border-purple-200 rounded-lg hover:bg-purple-50 transition-colors"
              >
                <div className="flex items-center space-x-3">
                  {conn.api_type === 'google_drive' && <Cloud className="w-5 h-5 text-blue-600" />}
                  {conn.api_type === 'gmail' && <Mail className="w-5 h-5 text-red-600" />}
                  {conn.api_type === 'notion' && <FileText className="w-5 h-5 text-gray-800" />}
                  {conn.api_type === 'dropbox' && <Cloud className="w-5 h-5 text-blue-600" />}
                  {conn.api_type === 'confluence' && <FileText className="w-5 h-5 text-blue-600" />}
                  {conn.api_type === 'slack' && <MessageSquare className="w-5 h-5 text-purple-600" />}
                  
                  <div>
                    <div className="font-bold text-purple-900">{conn.name}</div>
                    <div className="text-xs text-gray-500">
                      {conn.api_type} • {conn.status}
                    </div>
                  </div>
                </div>

                <div className="flex items-center space-x-2">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    conn.status === 'connected' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                  }`}>
                    {conn.status === 'connected' ? <CheckCircle className="w-3 h-3 inline mr-1" /> : <AlertCircle className="w-3 h-3 inline mr-1" />}
                    {conn.status}
                  </span>
                  
                  {/* Browse Files Button (solo per Google Drive e Dropbox) */}
                  {(conn.api_type === 'google_drive' || conn.api_type === 'dropbox') && (
                    <button
                      onClick={() => openFileBrowser(conn)}
                      className="bg-purple-600 hover:bg-purple-700 text-white px-3 py-1 rounded-lg text-xs font-medium transition-colors flex items-center"
                    >
                      <FileText className="w-3 h-3 mr-1" />
                      Browse Files
                    </button>
                  )}
                  
                  <button
                    onClick={() => handleDeleteConnection(conn.connection_id)}
                    className="text-red-600 hover:text-red-700 p-2 rounded-lg hover:bg-red-50 transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* 📂 FILE BROWSER */}
      {showFileBrowser && browserConnection && (
        <div className="bg-white border-2 border-indigo-300 rounded-xl shadow-xl p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-xl font-bold text-indigo-900 flex items-center">
                <FileText className="w-6 h-6 mr-2" />
                📂 File Browser - {browserConnection.name}
              </h3>
              <p className="text-sm text-indigo-700 mt-1">
                Seleziona i file da indicizzare per RAG
              </p>
            </div>
            <button
              onClick={() => setShowFileBrowser(false)}
              className="text-gray-500 hover:text-gray-700 text-2xl"
            >
              ✕
            </button>
          </div>

          {/* Index Name Input */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Nome Indice *
            </label>
            <input
              type="text"
              value={indexName}
              onChange={(e) => setIndexName(e.target.value)}
              placeholder="es: my_drive_docs"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
            />
          </div>

          {/* File List */}
          {isLoadingFiles ? (
            <div className="flex items-center justify-center py-12">
              <Loader className="w-8 h-8 text-indigo-600 animate-spin" />
              <span className="ml-3 text-indigo-700">Caricamento file...</span>
            </div>
          ) : (
            <>
              {/* Select All / Stats */}
              <div className="flex items-center justify-between mb-3 pb-3 border-b border-gray-200">
                <div className="flex items-center space-x-4">
                  <button
                    onClick={toggleSelectAll}
                    className="text-sm font-medium text-indigo-600 hover:text-indigo-800 flex items-center"
                  >
                    <input
                      type="checkbox"
                      checked={selectedFiles.size === cloudFiles.length && cloudFiles.length > 0}
                      readOnly
                      className="mr-2 w-4 h-4 text-indigo-600"
                    />
                    Seleziona Tutti ({cloudFiles.length})
                  </button>
                  <span className="text-sm text-gray-600">
                    {selectedFiles.size} file selezionati
                  </span>
                </div>
                <button
                  onClick={() => loadFiles(browserConnection)}
                  className="text-sm text-indigo-600 hover:text-indigo-800 flex items-center"
                >
                  <RefreshCw className="w-4 h-4 mr-1" />
                  Ricarica
                </button>
              </div>

              {/* File List with Checkboxes */}
              <div className="max-h-96 overflow-y-auto space-y-2 mb-4">
                {cloudFiles.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    Nessun file trovato
                  </div>
                ) : (
                  cloudFiles.map((file) => (
                    <div
                      key={file.id}
                      onClick={() => toggleFileSelection(file.id)}
                      className={`flex items-center p-3 border rounded-lg cursor-pointer transition-colors ${
                        selectedFiles.has(file.id)
                          ? 'bg-indigo-50 border-indigo-300'
                          : 'bg-white border-gray-200 hover:bg-gray-50'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={selectedFiles.has(file.id)}
                        readOnly
                        className="mr-3 w-4 h-4 text-indigo-600"
                      />
                      <FileText className="w-5 h-5 text-gray-500 mr-2 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-gray-900 truncate">{file.name}</div>
                        {file.mimeType && (
                          <div className="text-xs text-gray-500">{file.mimeType}</div>
                        )}
                      </div>
                      {file.size && (
                        <div className="text-xs text-gray-500 ml-2">
                          {(file.size / 1024 / 1024).toFixed(2)} MB
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>

              {/* Index Button */}
              <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                <div className="text-sm text-gray-600">
                  💡 I file saranno scaricati, processati e indicizzati per RAG
                </div>
                <button
                  onClick={handleIndexSelectedFiles}
                  disabled={isIndexing || selectedFiles.size === 0}
                  className="bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white px-6 py-3 rounded-lg font-bold transition-all shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                >
                  {isIndexing ? (
                    <>
                      <Loader className="w-5 h-5 mr-2 animate-spin" />
                      Indicizzazione in corso...
                    </>
                  ) : (
                    <>
                      <Zap className="w-5 h-5 mr-2" />
                      Indicizza {selectedFiles.size} File
                    </>
                  )}
                </button>
              </div>

              {/* Tracking ID Display */}
              {trackingId && (
                <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                  <div className="text-sm font-medium text-blue-900">
                    📊 Tracking ID: <code className="bg-blue-100 px-2 py-1 rounded">{trackingId}</code>
                  </div>
                  <div className="text-xs text-blue-700 mt-1">
                    L'indicizzazione è in corso. Riceverai una notifica al completamento.
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* 📚 Guide & Documentation */}
      <div className="bg-gradient-to-r from-blue-50 to-cyan-50 border-2 border-blue-200 rounded-xl p-6">
        <h3 className="text-lg font-bold text-blue-900 mb-3 flex items-center">
          <ExternalLink className="w-5 h-5 mr-2" />
          📚 Guide & Setup
        </h3>
        <div className="grid grid-cols-2 gap-3 text-sm">
          <a href="https://console.cloud.google.com/apis/credentials" target="_blank" rel="noopener noreferrer" className="flex items-center text-blue-600 hover:text-blue-800">
            <ExternalLink className="w-4 h-4 mr-1" /> Google Cloud Console
          </a>
          <a href="https://www.notion.so/my-integrations" target="_blank" rel="noopener noreferrer" className="flex items-center text-blue-600 hover:text-blue-800">
            <ExternalLink className="w-4 h-4 mr-1" /> Notion Integrations
          </a>
          <a href="https://www.dropbox.com/developers/apps" target="_blank" rel="noopener noreferrer" className="flex items-center text-blue-600 hover:text-blue-800">
            <ExternalLink className="w-4 h-4 mr-1" /> Dropbox Developer Apps
          </a>
          <a href="https://id.atlassian.com/manage-profile/security/api-tokens" target="_blank" rel="noopener noreferrer" className="flex items-center text-blue-600 hover:text-blue-800">
            <ExternalLink className="w-4 h-4 mr-1" /> Atlassian API Tokens
          </a>
          <a href="https://api.slack.com/apps" target="_blank" rel="noopener noreferrer" className="flex items-center text-blue-600 hover:text-blue-800">
            <ExternalLink className="w-4 h-4 mr-1" /> Slack App Management
          </a>
        </div>
      </div>
    </div>
  );
}

